<template>
  <UiDialog
    ref="dialog"
    class="w-3xl"
    animate
    :data-testid="testId"
    :has-close-button="hasCloseButton"
    :close-label="t('control.cancel')"
    @close="closed">
    <template v-if="$slots.header" #header>
      <slot name="header" />
    </template>

    <slot name="default" />

    <template #footer="{ close: closeModal }">
      <slot
        name="footer"
        :close="close"
        :submit="onSubmit"
        :submit-button-label="submitButtonLabel"
        :cancel-button-label="cancelButtonLabel">
        <UiButton
          appearance="outline-emphasis"
          class="w-full justify-center md:w-fit"
          data-testid="cancelButton"
          size="md"
          @click="
            closeReason = 'cancel';
            closeModal();
          ">
          {{ cancelButtonLabel ?? t("control.cancel") }}
        </UiButton>
        <UiButton
          ref="submitButtonEl"
          appearance="fill-emphasis"
          :disabled="submitDisabled"
          class="w-full justify-center md:w-fit"
          data-testid="submitButton"
          @click="onSubmit">
          <UiSpinner v-if="isLoading" appearance="neutral" />
          <span v-else>{{ submitButtonLabel ?? t("control.submit") }}</span>
        </UiButton>
      </slot>
    </template>
  </UiDialog>
</template>
<script lang="ts" setup>
import UiButton from "@dpa-id-components/dpa-shared-components/src/components/UiButton/UiButton.vue";
import UiDialog from "@dpa-id-components/dpa-shared-components/src/components/UiDialog/UiDialog.vue";
import UiSpinner from "@dpa-id-components/dpa-shared-components/src/components/UiSpinner/UiSpinner.vue";
import { nextTick, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import { useTracking } from "@/composition/shared/useTracking.ts";

const { t } = useI18n();

const {
  cancelButtonLabel = undefined,
  submitButtonLabel = undefined,
  testId = undefined,
  trackingPlace = undefined,
  trackingId = undefined,
  submitDisabled = false,
  hasCloseButton = true,
  isLoading,
} = defineProps<{
  testId?: string;
  trackingPlace?: string;
  trackingId?: string;
  hasCloseButton?: boolean;
  cancelButtonLabel?: string;
  submitButtonLabel?: string;
  submitDisabled?: boolean;
  isLoading?: boolean;
  closeOnClickOutside?: boolean;
}>();

const dialog = useTemplateRef<InstanceType<typeof UiDialog>>("dialog");

type CloseReason = "cancel" | "submit" | "dismiss" | null;
const closeReason = ref<CloseReason>(null);

const emit = defineEmits<{
  submit: [];
  closed: [];
}>();

const { trackEvent } = useTracking();
function trackClosed() {
  if (!trackingPlace) return;
  if (closeReason.value == "submit") {
    trackEvent(
      `${trackingPlace}:modal_click_button_${trackingId ? trackingId + "_" : ""}submit`,
      submitButtonLabel ?? t("control.submit"),
    );
  }
  if (closeReason.value == "dismiss") {
    trackEvent(
      `${trackingPlace}:modal_click_iconButton-clickaway-escape_${trackingId ? trackingId + "_" : ""}close`,
    );
  } else if (closeReason.value == "cancel") {
    trackEvent(
      `${trackingPlace}:modal_click_button_${trackingId ? trackingId + "_" : ""}cancel`,
      cancelButtonLabel ?? t("control.cancel"),
    );
  }
}

const onSubmit = async () => {
  emit("submit");
  closeReason.value = "submit";
  await nextTick();
  if (isLoading) {
    return;
  }
  dialog.value?.close();
};

function showModal() {
  closeReason.value = null;
  dialog.value?.showModal();
}

function requestClose() {
  dialog.value?.requestClose();
}

function close() {
  dialog.value?.close();
}

function closed() {
  if (closeReason.value == null) {
    closeReason.value = "dismiss";
  }
  trackClosed();
  emit("closed");
}

defineExpose({
  showModal,
  close,
  requestClose,
});
</script>
